import React, { useState, useEffect } from 'react';
import SelectField from './SelectField';
import { FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import Modal from './Modal';

const emailRegex = /\S+@\S+\.\S+/;

const UserFormModal = ({ open, onClose, onSubmit, title = 'User', groups = [], initial }) => {
  const [form, setForm] = useState({ username: '', email: '', password: '', groups: [] });

  useEffect(() => {
    if (!open) return;
    const init = initial || {};
    setForm({
      username: init.username || '',
      email: init.email || '',
      password: '',
      groups: init.groups || (groups.some((g) => (g?.GroupName || g) === 'QAAgent') ? ['QAAgent'] : [])
    });
    // intentionally omit `groups` from deps so user input isn't reset on update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const emailValid = emailRegex.test(form.email);
  const pwLength = form.password.length >= 8 || (initial?.username && !form.password);
  const pwSpaces = form.password === form.password.trim();
  const canSubmit = form.username && emailValid && pwLength && pwSpaces;

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div>
          <label htmlFor="username" className="block text-sm font-semibold text-gray-800 mb-1.5">Username</label>
          <input
            id="username"
            className="input-field"
            type="text"
            placeholder="Enter username"
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            disabled={!!initial?.username}
            required
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-1.5">Email Address</label>
          <input
            id="email"
            className="input-field"
            type="email"
            placeholder="name@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
          {!emailValid && form.email && (
            <p className="mt-1 text-sm text-red-600" aria-live="polite">Please enter a valid email address.</p>
          )}
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-semibold text-gray-800 mb-1.5">Password</label>
          <input
            id="password"
            className="input-field"
            type="password"
            placeholder="Set a password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required={!initial?.username}
          />
          <div className="mt-2 text-sm" aria-live="polite">
            <div>{pwLength ? '✓' : '✗'} At least 8 characters</div>
            <div>{pwSpaces ? '✓' : '✗'} No leading or trailing spaces</div>
          </div>
        </div>
        {groups && groups.length > 0 && (
          <div>
            <label htmlFor="groups" className="block text-sm font-semibold text-gray-800 mb-1.5">
              Assign Groups
            </label>
            <SelectField
              id="groups"
              multiple
              className="input-field min-h-[100px]" // Added height for better multi-select visibility
              value={form.groups}
              onChange={(e) =>
                setForm({ ...form, groups: Array.from(e.target.selectedOptions).map((o) => o.value) })
              }
            >
              {groups.map((g) => (
                // Handling both string arrays or object arrays from Cognito
                <option key={g.GroupName || g} value={g.GroupName || g}>
                  {g.GroupName || g}
                </option>
              ))}
            </SelectField>
            <p className="mt-1 text-xs text-gray-500 italic">Hold Ctrl (Windows) or Command (Mac) to select multiple.</p>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-outline"><FaTimes />{' '}Cancel</button>
          <button onClick={() => onSubmit(form)} disabled={!canSubmit} className="btn-primary disabled:opacity-50">
            {initial?.username ? <FaSave /> : <FaPlus />}{' '}
            {initial?.username ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UserFormModal;
